export const roundNumber = ({ number, decimalPlaces = 2 }: { number?: number; decimalPlaces?: number }): number => {
  if (!number) {
    return 0;
  }

  return parseFloat(number.toFixed(decimalPlaces));
};

// Numbers follow the region, not the UI language, as iOS does: a Norwegian worker reads "4,9" in an English UI.
const NUMBER_LOCALE = 'nb-NO';

export const formatDecimal = (value: number, fractionDigits: number): string =>
  new Intl.NumberFormat(NUMBER_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(value);

/** As many decimals as the value needs, up to `maxFractionDigits`: "50" and "33,33", never "50,00". */
export const formatDecimalUpTo = (value: number, maxFractionDigits: number): string =>
  new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: maxFractionDigits }).format(value);

const COMPACT_FROM = 10_000;

/** Compact notation with thousands as "12K": Intl's Norwegian "12k" is rarer in writing than a capital K. */
export const formatCompactNotation = (formatter: Intl.NumberFormat, value: number): string =>
  formatter
    .formatToParts(value)
    .map((part) => (part.type === 'compact' && part.value === 'k' ? 'K' : part.value))
    .join('');

/** "9 999", then "10K", "28,8K", "1,2 mill." -- a count that must fit a fixed, small space. */
export const formatCompactCount = (value: number): string =>
  Math.abs(value) < COMPACT_FROM
    ? formatDecimal(value, 0)
    : formatCompactNotation(
        new Intl.NumberFormat(NUMBER_LOCALE, { notation: 'compact', maximumFractionDigits: 1 }),
        value
      );

/** "850 m", "9,4 km", "91 km" -- one decimal only where it changes the reading. */
export const formatDistance = (meters: number): string => {
  const roundedMeters = Math.round(meters / 10) * 10;
  if (roundedMeters < 1000) {
    return `${roundedMeters} m`;
  }
  const kilometers = meters / 1000;
  // Decide the decimals on the rounded value, so 9 980 m reads "10 km", not "10,0 km".
  return `${formatDecimal(kilometers, Math.round(kilometers * 10) / 10 < 10 ? 1 : 0)} km`;
};
